import React, { useEffect, useState } from 'react';
import { Spinner } from '@components/common/Spinner';
import { buildingInfoService } from '@services/building-info.service';
import { apiClient } from '@services/api-client';
import { VacancyRateCard } from './VacancyRateCard';
import { Commune, PoiData, PoiResult } from '../../types/index';

interface CantonUnemployment { rate: number; period: string }

interface LocationTabProps {
  lat: number;
  lng: number;
  propertyId: string;
  commune?: Commune | null;
  rooms?: number | null;
}

const CATEGORY_CONFIG: Record<string, { icon: string; color: string; description: string }> = {
  bus_stop: {
    icon: '🚌',
    color: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
    description: 'Arrêt de bus le plus proche',
  },
  train_station: {
    icon: '🚆',
    color: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800',
    description: 'Gare la plus proche',
  },
  highway_ramp: {
    icon: '🛣️',
    color: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
    description: 'Accès autoroute le plus proche',
  },
  school: {
    icon: '🏫',
    color: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
    description: 'Ecole la plus proche',
  },
  kindergarten: {
    icon: '🧒',
    color: 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-800',
    description: 'Crèche / Garderie la plus proche',
  },
  grocery: {
    icon: '🛒',
    color: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    description: 'Commerce alimentaire le plus proche',
  },
  pharmacy: {
    icon: '💊',
    color: 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800',
    description: 'Pharmacie la plus proche',
  },
  hospital: {
    icon: '🏥',
    color: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    description: 'Hôpital / Médecin le plus proche',
  },
  park: {
    icon: '🌳',
    color: 'bg-lime-50 dark:bg-lime-900/20 border-lime-200 dark:border-lime-800',
    description: 'Parc / Jardin le plus proche',
  },
  water: {
    icon: '🏞️',
    color: 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-800',
    description: "Lac / Plan d'eau le plus proche",
  },
  forest: {
    icon: '🌲',
    color: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
    description: 'Forêt la plus proche',
  },
};

const fmtDistance = (meters: number): string =>
  meters < 1000 ? `${meters} m` : `${(meters / 1000).toFixed(1)} km`;

const getDistanceBadgeColor = (category: string, meters: number): string => {
  if (['bus_stop', 'train_station'].includes(category)) {
    if (meters < 300) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    if (meters < 800) return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300';
    if (meters < 2000) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
    return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
  }
  return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
};

const PoiCard: React.FC<{ poi: PoiResult }> = ({ poi }) => {
  const config = CATEGORY_CONFIG[poi.category] ?? {
    icon: '📍',
    color: 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700',
    description: poi.label,
  };
  return (
    <div className={`rounded-xl border p-4 ${config.color}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{config.icon}</span>
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {config.description}
            </p>
            <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-0.5">
              {poi.name}
            </p>
          </div>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-sm font-bold flex-shrink-0 ${getDistanceBadgeColor(
            poi.category,
            poi.distance,
          )}`}
        >
          {fmtDistance(poi.distance)}
        </span>
      </div>
    </div>
  );
};

const MissingPoiCard: React.FC<{ category: string }> = ({ category }) => {
  const config = CATEGORY_CONFIG[category];
  if (!config) return null;
  return (
    <div className="rounded-xl border border-dashed border-gray-200 dark:border-gray-700 p-4 opacity-50">
      <div className="flex items-center gap-3">
        <span className="text-2xl">{config.icon}</span>
        <p className="text-sm text-gray-400 dark:text-gray-500">
          {config.description} — aucun résultat
        </p>
      </div>
    </div>
  );
};

const TRANSPORT_CATS = ['bus_stop', 'train_station', 'highway_ramp'];
const SERVICE_CATS = ['school', 'kindergarten', 'grocery', 'pharmacy'];
const HEALTH_CATS = ['hospital'];
const NATURE_CATS = ['park', 'water', 'forest'];

// ─── Commune indicators ───────────────────────────────────────────────────────

type TrendDir = 'up' | 'down' | 'stable';

const calcTrend = (current: number, prev: number): TrendDir => {
  const diff = current - prev;
  const pct = Math.abs(diff / (prev || 1));
  if (pct < 0.005) return 'stable';
  return diff > 0 ? 'up' : 'down';
};

const TrendBadge: React.FC<{ dir: TrendDir; label: string; positive: TrendDir }> = ({
  dir,
  label,
  positive,
}) => {
  const isGood = dir === positive || dir === 'stable';
  const color =
    dir === 'stable'
      ? 'text-gray-500 dark:text-gray-400'
      : isGood
        ? 'text-emerald-600 dark:text-emerald-400'
        : 'text-red-500 dark:text-red-400';
  const arrow = dir === 'up' ? '↑' : dir === 'down' ? '↓' : '→';
  return (
    <span className={`text-xs font-medium ${color}`}>
      {arrow} {label}
    </span>
  );
};

const fmtPop = (n: number) =>
  n >= 1000 ? `${(n / 1000).toFixed(1).replace('.', '\'')}\u202fk` : String(n);

const fmtChf = (n: number) =>
  `CHF\u202f${Math.round(n / 1000)}\u202fk / an`;

const fmtPeriod = (period: string) => {
  const [yyyy, mm] = period.split('-');
  const months = ['jan.','fév.','mars','avr.','mai','juin','juil.','août','sept.','oct.','nov.','déc.'];
  const idx = parseInt(mm, 10) - 1;
  return `${months[idx] ?? mm} ${yyyy}`;
};

const CommuneIndicators: React.FC<{ commune: Commune; cantonUnemployment?: CantonUnemployment | null }> = ({ commune, cantonUnemployment }) => {
  const popTrend =
    commune.population != null && commune.populationPrev != null
      ? calcTrend(commune.population, commune.populationPrev)
      : null;

  const popPct =
    commune.population != null && commune.populationPrev != null && commune.populationPrev > 0
      ? (((commune.population - commune.populationPrev) / commune.populationPrev) * 100).toFixed(1)
      : null;

  const revTrend =
    commune.incomeMedian != null && commune.incomeMedianPrev != null
      ? calcTrend(commune.incomeMedian, commune.incomeMedianPrev)
      : null;

  const revPct =
    commune.incomeMedian != null && commune.incomeMedianPrev != null && commune.incomeMedianPrev > 0
      ? (((commune.incomeMedian - commune.incomeMedianPrev) / commune.incomeMedianPrev) * 100).toFixed(1)
      : null;

  return (
    <div className="rounded-xl border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/50 dark:bg-indigo-950/20 p-4">
      <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide mb-3">
        Indicateurs communaux — {commune.name}
        <span className="ml-2 text-xs font-normal text-gray-400 dark:text-gray-500 normal-case">
          Canton {commune.canton} · NPA {commune.npa}
        </span>
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {/* Population */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Population</p>
          {commune.population != null ? (
            <>
              <p className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {fmtPop(commune.population)} hab.
              </p>
              <div className="mt-1">
                {popTrend && popPct ? (
                  <TrendBadge
                    dir={popTrend}
                    label={`${popPct}%/an`}
                    positive="up"
                  />
                ) : (
                  <span className="text-xs text-gray-400">N/D</span>
                )}
              </div>
              {commune.populationYear && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Données {commune.populationYear}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">N/D</p>
          )}
        </div>

        {/* Chômage — taux cantonal SECO (mensuel) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Taux de chômage</p>
          {cantonUnemployment ? (
            <>
              <p className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {cantonUnemployment.rate.toFixed(1)}&thinsp;%
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                Canton {commune.canton} · {fmtPeriod(cantonUnemployment.period)}
              </p>
            </>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">N/D</p>
          )}
        </div>

        {/* Revenu médian */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Revenu médian</p>
          {commune.incomeMedian != null ? (
            <>
              <p className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {fmtChf(commune.incomeMedian)}
              </p>
              <div className="mt-1">
                {revTrend && revPct ? (
                  <TrendBadge
                    dir={revTrend}
                    label={`${revPct}%/an`}
                    positive="up"
                  />
                ) : (
                  <span className="text-xs text-gray-400">N/D</span>
                )}
              </div>
              {commune.incomeYear && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Données {commune.incomeYear}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">N/D</p>
          )}
        </div>
      </div>
    </div>
  );
};

export const LocationTab: React.FC<LocationTabProps> = ({ lat, lng, propertyId, commune, rooms }) => {
  const [data, setData] = useState<PoiData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cantonUnemployment, setCantonUnemployment] = useState<CantonUnemployment | null>(null);

  const load = async (force = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await buildingInfoService.fetchPois(lat, lng, propertyId, force);
      setData(result);
    } catch {
      setError(
        'Impossible de charger les données de localisation (OpenStreetMap indisponible)',
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { load(); }, [lat, lng, propertyId]);

  useEffect(() => {
    if (!commune?.canton) return;
    apiClient
      .get<CantonUnemployment | null>(`/communes/canton-unemployment/${commune.canton}`)
      .then((d) => setCantonUnemployment(d))
      .catch(() => setCantonUnemployment(null));
  }, [commune?.canton]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
        <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
          Chargement des points d'intérêt...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const foundCategories = new Set(data.pois.map((p) => p.category));

  const renderSection = (title: string, categories: string[]) => {
    const found = data.pois.filter((p) => categories.includes(p.category));
    const missing = categories.filter((c) => !foundCategories.has(c));
    return (
      <div>
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          {title}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {found.map((poi) => (
            <PoiCard key={poi.category} poi={poi} />
          ))}
          {missing.map((c) => (
            <MissingPoiCard key={c} category={c} />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {commune && (
        <CommuneIndicators commune={commune} cantonUnemployment={cantonUnemployment} />
      )}

      {commune?.bfsNr && (
        <VacancyRateCard
          bfsNr={commune.bfsNr}
          defaultRooms={rooms != null && rooms >= 1
            ? rooms >= 6 ? '6+' : String(Math.floor(rooms))
            : '_T'}
        />
      )}

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Source : OpenStreetMap (Overpass API) · Coordonnées : {lat.toFixed(5)}, {lng.toFixed(5)}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {data.pois.length} point{data.pois.length !== 1 ? 's' : ''} trouvé{data.pois.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={() => load(true)}
            disabled={isLoading}
            className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
          >
            <svg className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Actualiser
          </button>
        </div>
      </div>

      {renderSection('Transport', TRANSPORT_CATS)}
      {renderSection('Services', SERVICE_CATS)}
      {renderSection('Santé', HEALTH_CATS)}
      {renderSection('Nature', NATURE_CATS)}
    </div>
  );
};
